import { NestedTreeControl } from '@angular/cdk/tree';
import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { ToastrService } from 'ngx-toastr';
import { DialogService } from 'src/app/services/dialog.service';
import { EquipmentManagenementService } from 'src/app/services/equipmentmanagement.service';
import { AddLevelOtaComponent } from '../../otasettings/otasettings-add/otasettings-add.component';
import { LevelHierarchyDepth } from 'src/app/utils/utils';

interface LevelNode {
    selected?: boolean;
    expandable?: boolean;
    parent?: LevelNode;

    level?: OtaSettingsLevel;

    id: string;
    text: string;
    children: LevelNode[];
}

@Component({
    selector: 'otasettings-levels',
    standalone: false,
    templateUrl: './otasettings-levels.component.html',
    styleUrls: ['./otasettings-levels.component.less'],
    encapsulation: ViewEncapsulation.None
})
export class OtaSettingsLevelsComponent implements OnInit {

    @Output()
    public levelSelectedTrigger = new EventEmitter<OtaSettingsLevel>();
    public selectedLevel: OtaSettingsLevel | undefined;
    public treeData: LevelNode[] = [];
    public treeControl = new NestedTreeControl<LevelNode>((node) => node.children);
    public dataSource = new MatTreeNestedDataSource<LevelNode>();
    private userLevels: OtaSettingsLevel[] = [];
    //Filters
    public searchString = '';
    public showOnlySelected = false;
    //UI
    private dialogRef: MatDialogRef<AddLevelOtaComponent, any> | undefined

    @Input() maxDepth: LevelHierarchyDepth = LevelHierarchyDepth.Device;

    constructor(
        private readonly emtService: EquipmentManagenementService,
        private readonly dialogForm: MatDialog,
        private readonly dialog: DialogService,
        private readonly toast: ToastrService
    ) {
    }

    ngOnInit() {
        this.emtService.initOtaSettings(
            () => this.loadOtaLevels(),
            () => this.loadOtaLevels()
        );
    }

    private loadOtaLevels(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.emtService.getOtaLevels(
                response => {
                    this.userLevels = response;
                    this.updateTreeFromSource();
                    resolve();
                },
                error => {
                    this.toast.error('Unable to load user levels.');
                    reject(error);
                }
            );
        });
    }

    hasChild =
        (_: number, node: LevelNode) =>
            !!node.children && node.children.length > 0;

    addNewLevel(): void {
        this.dialogRef = this.dialogForm.open(AddLevelOtaComponent, {
            disableClose: true,
            width: '850px',
            panelClass: ['custom-mat-dialog_v2'],
            data: { maxDepth: this.maxDepth },
        });
        this.dialogRef.componentInstance.levelInitializedTrigger
            .subscribe((level: EquipmentConfigurationLevelDetail) => {
                this.loadOtaLevels().then(() => {
                    // Find created level
                    let selectionNode = this.findNode(level);
                    if (selectionNode) {
                        this.onYes(selectionNode);
                        this.expandTreeToNode(selectionNode);
                    }
                }).catch(error => {
                    console.error('Error loading user levels');
                });
            });
    }

    private findNode(level: EquipmentConfigurationLevelDetail) {
        if (this.emtService.isEnvironment(level)) {
            return this.treeData.find(x => x.id.toLowerCase() === level.environment) ?? null;
        }
        if (level.tenantId) {
            return this.treeData.find(x => x.id.toLowerCase() === level.environment)
                ?.children.find(y => y.id === level.tenantId!) ?? null;
        }
        return null;
    }

    private expandTreeToNode(node: LevelNode): void {
        if (node.parent) {
            this.expandTreeToNode(node.parent);
        }
        this.treeControl.expand(node);
    }

    onSelectNode(node: LevelNode) {
        if (!node.level) {
            return;
        }
        if (node.level.environment === this.selectedLevel?.environment &&
            node.level.tenantId === this.selectedLevel?.tenantId) {
            return;
        }
        this.dialog.showConfirmDialog(
            "Do you want to change to the level " +
            this.levelToString(node.level) + "?",
            "Confirm",
            this,
            node);
    }

    onYes(node: LevelNode) {
        this.unSelectPrevious();
        this.selectedLevel = node.level
            ? { environment: node.level.environment, tenantId: node.level.tenantId }
            : undefined;
        this.selectNode(node);
        if (this.selectedLevel) {
            this.levelSelectedTrigger.emit(this.selectedLevel);
        }
    }

    unSelectPrevious() {
        this.treeData.forEach(x => {
            this.unSelectTree(x);
        });
    }
    selectNode(node?: LevelNode) {
        if (!node)
            return

        node.selected = true;
        this.selectNode(node.parent);
    }

    unSelectTree(node: LevelNode) {
        node.selected = false;
        node.children.forEach(x => {
            this.unSelectTree(x);
        });
    }

    public hideLeafNode(node: LevelNode): boolean {
        return new RegExp(this.searchString, 'i').test(node.text) === false;
    }

    public hideParentNode(node: LevelNode): boolean {
        if (
            !this.searchString ||
            node.text.toLowerCase().includes(this.searchString.toLowerCase())
        ) {
            return false
        }
        const descendants = this.treeControl.getDescendants(node)

        return !(descendants.some(
            (descendantNode) =>
                descendantNode.text
                    .toLowerCase()
                    .includes(this.searchString.toLowerCase())));
    }

    private levelToString(level: OtaSettingsLevel): string {
        let result = "( ";
        result += `Environment: ${level.environment}`;
        result += level.tenantId ? `, Tenant: ${level.tenantId}` : '';
        result += " )";
        return result;
    }

    //Tree related methods
    private updateTreeFromSource(): void {
        const nodeRoots: LevelNode[] = [];
        this.userLevels.forEach(level => {
            const envLevel: OtaSettingsLevel = { environment: level.environment };
            const environment = this.getOrCreateNode(nodeRoots, level.environment, level.environment, envLevel);

            if (level.tenantId) {
                const tenantLevel: OtaSettingsLevel = { environment: level.environment, tenantId: level.tenantId };
                this.getOrCreateNode(environment.children, level.tenantId, level.tenantId, tenantLevel, environment);
            }
        });

        this.treeData = nodeRoots;
        this.treeControl.dataNodes = this.treeData;
        this.dataSource.data = this.treeData;
    }

    private createNode(
        id: string,
        text: string,
        detail?: OtaSettingsLevel,
        parent?: LevelNode): LevelNode {
        return {
            id,
            text,
            level: detail,
            children: [],
            parent: parent
        };
    }

    private getOrCreateNode(
        nodes: LevelNode[],
        id: string,
        name: string,
        levelData?: OtaSettingsLevel,
        parent?: LevelNode): LevelNode {
        let node = nodes.find(n => n.id === id);
        if (!node) {
            node = this.createNode(id, name, levelData, parent);
            nodes.push(node);
        }
        return node;
    }
    //Tree related methods end
}
